
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import CameraPreview, { getCurrentCameraPreview } from '@/components/CameraPreview.vue';
import {
  DirectionDetectionPhase,
  FaceTrackerServer,
  Direction,
  TrackerStatus,
  TrackerType,
  ErrorType,
  FaceResult,
  FPS,
  CalibrationResult,
  CalibrationStatus
} from '@/services/facetracker';
import { CheckDirectionStatus } from '@/api/facetracker/FaceTracker';
import { ScreenOrientation } from '@/resources/enum/ScreenOrientation';

export interface TrackerErrorEventArgs {
  errorType: ErrorType;
  isManyRetry: boolean;
}

/** 首振りの状態を管理するレイヤー */
@Component({
  components: { CameraPreview }
})
export default class FaceDirectionTracker extends Vue {
  @Prop({ default: TrackerType.Server })
  trackerType!: TrackerType;

  @Prop({ type: Boolean, required: true })
  public isLoading!: boolean;

  private tracker!: FaceTrackerServer;

  private statusInternal = TrackerStatus.None;

  private set status(status: TrackerStatus) {
    if (this.statusInternal !== status) {
      this.statusInternal = status;
      this.statusChanged();
    }
  }

  private get status(): TrackerStatus {
    return this.statusInternal;
  }

  private directionPhaseInternal = DirectionDetectionPhase.None;

  private set directionPhase(step: DirectionDetectionPhase) {
    if (this.directionPhaseInternal !== step) {
      this.directionPhaseInternal = step;
      this.directionPhaseChanged();
    }
  }

  private get directionPhase(): DirectionDetectionPhase {
    return this.directionPhaseInternal;
  }

  private directionStatusInternal = CheckDirectionStatus.None;

  private set directionStatus(status: CheckDirectionStatus) {
    if (this.directionStatusInternal !== status) {
      this.directionStatusInternal = status;
      this.directionStatusChanged();
    }
  }

  private get directionStatus(): CheckDirectionStatus {
    return this.directionStatusInternal;
  }

  private fps = new FPS(10);

  async mounted() {
    const preview: CameraPreview | null = getCurrentCameraPreview();
    if (preview === null) throw new Error('camera preview is null');

    switch (this.trackerType) {
      case TrackerType.Server:
        this.tracker = new FaceTrackerServer(preview.video, document.createElement('canvas'));
        break;
      default:
        console.error(`Tracker type: ${this.trackerType} is not supported`); // eslint-disable-line no-console
    }
    await this.tracker.load();

    const videoWidth: number = getCurrentCameraPreview()?.video.videoWidth ?? 0;
    const videoHeight: number = getCurrentCameraPreview()?.video.videoHeight ?? 0;
    const isSuccess: boolean = await this.tracker.videoInitialized(videoWidth, videoHeight);
    if (!isSuccess) throw new Error('FaceDirectionTracker initialized');
    this.status = TrackerStatus.EstablishingSession;
  }

  beforeDestroy() {
    this.tracker.dispose();
  }

  get preview(): CameraPreview {
    const preview: CameraPreview | null = getCurrentCameraPreview();
    if (preview === null) throw new Error('camera preview is null');
    return preview;
  }

  get currentStatus(): TrackerStatus {
    return this.status;
  }

  get currentDirectionStatus(): CheckDirectionStatus {
    return this.directionStatus;
  }

  get isInvalidOrientation(): boolean {
    const screenOrientation: ScreenOrientation = this.$store.state.orientation.orientation;
    return screenOrientation === ScreenOrientation.Landscape;
  }

  @Watch('isInvalidOrientation')
  private onOrientationChange(value: boolean): void {
    if (!value) return;
    this.status = TrackerStatus.InError;
    this.error({ errorType: ErrorType.InvalidScreenOrientation, isManyRetry: false });
  }

  calibrate() {
    if (this.status === TrackerStatus.Calibrating) {
      this.status = TrackerStatus.Detecting;
    }
  }

  public async reInitialized() {
    const isSuccess: boolean = await this.tracker.videoReInitialized();
    if (!isSuccess) throw new Error('FaceDirectionTracker reInitialized');
  }

  reset() {
    if (this.isInvalidOrientation) {
      this.status = TrackerStatus.InError;
      this.error({ errorType: ErrorType.InvalidScreenOrientation, isManyRetry: false });
      return;
    }

    this.status = TrackerStatus.EstablishingSession;
    this.directionPhase = DirectionDetectionPhase.None;
    this.directionStatus = CheckDirectionStatus.None;
    this.directionsChanged([]);
  }

  async update() {
    switch (this.status) {
      case TrackerStatus.None:
      case TrackerStatus.Ended:
        return;

      case TrackerStatus.EstablishingSession:
        this.status = TrackerStatus.Calibrating;
        return;

      case TrackerStatus.Calibrating: {
        const result: CalibrationResult = await this.tracker.calibrate();
        this.calibrateProgress(result.currentCount, result.maxCount);
        switch (result.status) {
          case CalibrationStatus.Processing:
            return;

          case CalibrationStatus.Completed:
            this.status = TrackerStatus.TakingSelfie;
            return;

          case CalibrationStatus.InError:
          default: {
            this.$store.commit('camera/addRetryCount');
            const isManyRetry: boolean = result.isManyRetry || this.$store.state.camera.retryCount >= 5;
            this.status = TrackerStatus.InError;
            this.error({ errorType: result.error || ErrorType.Unknown, isManyRetry });
            return;
          }
        }
      }

      case TrackerStatus.TakingSelfie: {
        const result = await this.tracker.takeSelfie();
        this.calibrateProgress(0, 1);
        if (result.isSuccess) {
          this.status = TrackerStatus.InitializingDirections;
        } else {
          this.status = TrackerStatus.InError;
          this.error({ errorType: result.error || ErrorType.Unknown, isManyRetry: false });
        }
        return;
      }

      case TrackerStatus.InitializingDirections: {
        const result = await this.tracker.initializeDirections();
        if (!result.error) {
          this.directionsChanged(result.directions.sort((a, b) => a.index - b.index).map((a) => a.direction));
          this.status = TrackerStatus.Detecting;
        } else {
          this.status = TrackerStatus.InError;
          this.error({ errorType: result.error || ErrorType.Unknown, isManyRetry: false });
        }
        return;
      }

      case TrackerStatus.Detecting: {
        if (this.directionPhase === DirectionDetectionPhase.None) {
          this.directionPhase = DirectionDetectionPhase.Phase1;
          return;
        }

        const result: FaceResult = await this.tracker.detect(this.directionPhase);

        switch (result.result) {
          case CheckDirectionStatus.Success:
            this.directionStatus = result.result;
            this.incrementDirectionPhase();
            if (this.directionPhase === DirectionDetectionPhase.Completed) {
              this.status = TrackerStatus.Ended;
            }
            this.directionStatus = CheckDirectionStatus.None; // reset
            return;

          case CheckDirectionStatus.Ignore:
          case CheckDirectionStatus.None:
            this.directionStatus = result.result;
            return;

          case CheckDirectionStatus.DifferentDirection:
          case CheckDirectionStatus.FaceNotFound: {
            this.$store.commit('camera/addRetryCount');
            const isManyRetry: boolean = result.isManyRetry || this.$store.state.camera.retryCount >= 5;
            this.directionStatus = result.result;
            this.status = TrackerStatus.InError;
            this.error({ errorType: result.error || ErrorType.Unknown, isManyRetry });
            return;
          }

          case CheckDirectionStatus.InError:
          default: {
            this.$store.commit('camera/addRetryCount');
            const isManyRetry: boolean = result.isManyRetry || this.$store.state.camera.retryCount >= 5;
            this.status = TrackerStatus.InError;
            this.error({ errorType: result.error || ErrorType.Unknown, isManyRetry });
            return;
          }
        }
      }
    }
  }

  @Emit()
  private error(e: TrackerErrorEventArgs) {
    //
    this.calibrateProgress(0, 1);
  }

  @Emit()
  private statusChanged() {
    return this.status;
  }

  @Emit()
  private directionPhaseChanged() {
    return this.directionPhase;
  }

  @Emit()
  private directionStatusChanged() {
    return this.directionStatus;
  }

  @Emit()
  private directionsChanged(value: Direction[]) {}

  @Emit()
  private calibrateProgress(currentCount: number, maxCount: number) {}

  private incrementDirectionPhase() {
    switch (this.directionPhase) {
      case DirectionDetectionPhase.None:
        this.directionPhase = DirectionDetectionPhase.Phase1;
        return;
      case DirectionDetectionPhase.Phase1:
        this.directionPhase = DirectionDetectionPhase.Center1;
        return;
      case DirectionDetectionPhase.Center1:
        this.directionPhase = DirectionDetectionPhase.Phase2;
        return;
      case DirectionDetectionPhase.Phase2:
        this.directionPhase = DirectionDetectionPhase.Center2;
        return;
      case DirectionDetectionPhase.Center2:
        this.directionPhase = DirectionDetectionPhase.Phase3;
        return;
      case DirectionDetectionPhase.Phase3:
        this.directionPhase = DirectionDetectionPhase.Completed;
        return;
      case DirectionDetectionPhase.Completed:
        return;
      default:
        return;
    }
  }
}
